<template>
  <Transition :name="reverse ? 'slide-reverse' : 'slide'" mode="out-in">
    <div aria-atomic="true" :aria-label="label" aria-live="polite" class="checkout-content" id="checkout-content" :key="screenId">
      <div class="checkout-content-inner" id="checkout-content-inner">
        <CheckoutLoading v-if="isLoading" class="loading-skeleton" />
        <Screen v-else :wide="isWideScreen">
          <ErrorJumbotron />
          <RouterView @navigateNext="handleNavigateNext" />
        </Screen>
      </div>
    </div>
  </Transition>
</template>
<script setup lang="ts">
import { useAmplitude } from '@outputinc/o-analytics'
import { ref, computed, onMounted, watch } from 'vue'
import CheckoutLoading from '@/components/shared/CheckoutLoading.vue'
import ErrorJumbotron from '@/components/shared/ErrorJumbotron.vue'
import { storeToRefs } from 'pinia'
import { useScreenStore } from '@/store/ScreenStore'
import { useProductStore } from '@/store/ProductStore'
import Screen from '@/components/shared/screens/Screen.vue'
import { ProductId } from '@/types'
const screenStore = useScreenStore()
const productStore = useProductStore()
const { currentScreen, screenIndex, isConfirmationScreen } = storeToRefs(screenStore)
const { productId } = storeToRefs(productStore)
const { navigateNext, navigatePrevious } = screenStore
const { track, Events } = useAmplitude()

const label = computed(() => currentScreen.name)
const screenId = computed(() => currentScreen.id)
const isWideScreen = computed(() => productId.value === ProductId.OutputOne && isConfirmationScreen.value)

const handleNavigateNext = () => navigateNext()

const isLoading = computed(() => false)

const timeout = ref(0)
const offset = ref(0)
const reverse = ref(false)
const noScrolling = ref(false)

const wheelNav = () => {
  if (!currentScreen.value) {
    return false
  }

  const checkoutContentElement = document.getElementById('checkout-content')

  if (checkoutContentElement) {
    checkoutContentElement.onwheel = event => {
      const content = document.getElementById('checkout-content') as HTMLElement
      const contentInner = document.getElementById('checkout-content-inner') as HTMLElement
      // If inner content height is greater than the content height.
      if (content.offsetHeight < contentInner.offsetHeight) {
        if (event.deltaY < 0 && Math.ceil(contentInner.getBoundingClientRect().y) < Math.ceil(content.getBoundingClientRect().y)) {
          return true
        }
        if (event.deltaY > 0 && Math.ceil(contentInner.getBoundingClientRect().bottom) > Math.ceil(content.getBoundingClientRect().bottom)) {
          return true
        }
      }

      const deltaY = event.deltaY
      event.preventDefault()
      window.clearTimeout(timeout.value)
      if (noScrolling.value || deltaY > 0 || (screenIndex.value === 0 && deltaY < 0)) {
        return false
      }
      const threshold = 100
      offset.value = offset.value > deltaY ? offset.value + deltaY : deltaY
      const eventName = Events.CHECKOUT_SUBSCRIPTION_UI_NAVIGATE
      const eventProperties = { source: 'scroll', direction: '' }
      if (offset.value <= -threshold) {
        eventProperties.direction = 'previous'
        track(eventName, eventProperties)
        navigatePrevious()
        offset.value = 0
      }
      if (offset.value >= threshold) {
        eventProperties.direction = 'next'
        track(eventName, eventProperties)
        navigateNext()
        offset.value = 0
      }
      timeout.value = window.setTimeout(() => {
        offset.value = 0
      }, 0)
    }
  }
}

onMounted(() => wheelNav())

watch(screenIndex, (newScreenIdx, oldScreenIdx) => {
  offset.value = 0
  reverse.value =
    oldScreenIdx.value && newScreenIdx.value ? newScreenIdx.value < oldScreenIdx.value && Math.abs(newScreenIdx.value - oldScreenIdx.value) === 1 : false
  noScrolling.value = true
  window.setTimeout(() => {
    noScrolling.value = false
  }, 0)
})
</script>
<style lang="scss" scoped>
@import '@/assets/styles/inc/variables';

.checkout-content {
  bottom: 0;
  left: 0;
  overflow: scroll;
  position: absolute;
  right: 0;
  scrollbar-width: none;
  top: 84px;
  z-index: 1;

  .loading-skeleton {
    padding-top: $spacer-9;
  }

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: none !important;
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  p {
    margin-bottom: $spacer-5;
  }
}

/* Slide transition animations */
.slide-enter-active,
.slide-leave-active,
.slide-reverse-enter-active,
.slide-reverse-leave-active {
  transition:
    transform 300ms ease,
    opacity 300ms ease;
}

.slide-enter-from {
  transform: translateY(30px);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(-30px);
  opacity: 0;
}

.slide-reverse-enter-from {
  transform: translateY(-30px);
  opacity: 0;
}

.slide-reverse-leave-to {
  transform: translateY(30px);
  opacity: 0;
}
</style>
